<template>
	<div ref="postGrid" class="dc-post-grid-component-wrapper">
		<header class="dc-post-grid-header" slot="dc-post-grid-header">
			<div
				v-if="title || subtitle"
				class="dc-post-grid-top-left"
				slot="dc-post-grid-top-left"
			>
				<h2 v-if="title" class="section-title-default">{{ title }}</h2>
				<h3 v-if="subtitle" class="section-subtitle-default">{{ subtitle }}</h3>
			</div>
			<div
				v-if="filters || search_form_placeholder"
				class="dc-post-grid-top-right"
				slot="dc-post-grid-top-right"
			>
				<a
					v-if="link && typeof link.url !== 'undefined'"
					:href="link.url"
					:target="link.target"
					:class="link.class || ''"
					>{{ link.title }}</a
				>
				<Filters
					v-if="currentPaged !== null"
					:filterLabel="label_before_filters"
					:filters="filters"
					:initialModel="initial_model"
					:currentPaged="currentPaged"
					:searchButtonId="searchButtonId"
					:searchFormPlaceholder="search_form_placeholder"
					:gridSettings="grid_settings"
					v-on:results="updateResults"
					v-on:searching="triggerLoading"
					v-on:update-total-pages="updateTotalPages"
					v-on:update-total-posts="updateTotalPosts"
					v-on:update-current-paged="updateCurrentPaged"
					v-on:update-post-grid-min-height="updateMinHeight"
				/>
			</div>
		</header>
		<div
			v-if="results"
			ref="resultsSection"
			:class="['dc-post-grid-results', 'uses-no-image-card-fallbacks', columnsClass]"
			slot="dc-post-grid-results"
		>
			<DcPostGridResults
				:results="results"
				:loading="loading"
				:gridLayout="grid_layout"
				:searchButtonId="searchButtonId"
				:showLoadMore="showLoadMore"
				:currentPaged="currentPaged || null"
				:totalPages="totalPages"
				:totalPosts="totalPosts"
				:usesPagination="usesPagination" 
				:postNavigationType="postNavigationType"
				:endOfResults="endOfResults"
				v-on:update-current-paged="updateCurrentPaged"
				v-on:load-more="loadMore"
			/>
		</div>
		<footer class="dc-post-grid-footer" slot="dc-post-grid-footer"> </footer>
	</div>
</template>

<script>
import DcPostGridResults from './DcPostGridResults';
import Filters from '@dc-post-grid/layout/Filters';
export default {
	props: {
		title: {
			type: String,
			required: false,
		},
		subtitle: {
			type: String,
			required: false,
		},
		filters: {
			type: Array,
			required: false,
		},
		link: {
			type: Array,
			required: false,
		},
		label_before_filters: {
			type: String,
			required: false,
		},
		initial_results: {
			type: Array,
			required: false,
		},
		initial_model: {
			type: Object,
			required: false,
		},
		posts_per_page: {
			type: Array,
			required: false,
		},
		grid_settings: {
			type: Array,
			required: false,
		},
		grid_layout: {
			type: String,
			default: 'default',
			required: false,
		},
		grid_columns: {
			type: Number,
			default: 3,
			required: false,
		},
		show_load_more: {
			type: Boolean,
			default: false,
			required: false,
		},
		search_form_placeholder: {
			type: String | Boolean,
			default: false,
			required: false,
		},
	},
	data() {
		return {
			loading: false,
			currentPaged: null,
			endOfResults: false,
			results: [],
		};
	},
	components: {
		DcPostGridResults,
		Filters,
	},
	computed: {
		usesPagination() {
			if (
				typeof this.initial_model !== 'undefined' &&
				( 
					this.initial_model.post_navigation_type === 'pagination' || 
					this.initial_model.post_navigation_type === 'pager'
				) 
			) {
				return true;
			}
			return false;
		},
		postNavigationType() {
			if (
				typeof this.initial_model !== 'undefined' 
			) {
				return this.initial_model.post_navigation_type;
			}
			return 'none';
		},
		showLoadMore() {
			// If pagination is set rather than load more, show the page navigation/loadmore anyway
			// Ignore the fact that this function is called showLoadMore, it doesn't make any sense.
			if (this.usesPagination) return true;

			// check if number of total results equals to post count, if so, don't show load more
			if (
				typeof this.initial_model !== 'undefined' && this.initial_model.end_of_results
			) return false;

			if (
				typeof this.initial_model === 'undefined' ||
				typeof this.initial_model.posts_per_page === 'undefined'
			)
			return this.show_load_more;
			return this.show_load_more && this.results.length >= this.initial_model.posts_per_page;
		},
		searchButtonId() {
			return 'dc-post-grid-search-' + Math.floor(Math.random() * 10000 + 1);
		},
		columnsClass() {
			return 'has-' + this.grid_columns + '-columns';
		},
	},
	methods: {
		triggerLoading() {
			this.loading = true;
		},
		updateCurrentPaged(newPagedNumber) {
			this.currentPaged = newPagedNumber;
		},
		updateTotalPages(newTotalPageNumber) {
			this.totalPages = newTotalPageNumber;
		},
		updateTotalPosts(newTotalPostNumber) {
			this.totalPosts = newTotalPostNumber;
		},
		updateFilter(key, value) {
			this.loading = true;
			this.endOfResults = false;
			if (this.usesPagination) {
				this.currentPaged = 1;
			}
		},
		updateResults(data) {
			this.loading = false;
			this.endOfResults = data.endOfResults;
			if (data.needsRefresh === true || this.usesPagination) {
				this.results = [];
				this.results = data.posts;
			} else {
				this.results = this.results.concat(data.posts);
			}
			if (this.usesPagination) {
				const y =
					this.$refs.resultsSection.getBoundingClientRect().top +
					window.pageYOffset -
					150;
				window.scrollTo({ top: y, behavior: 'smooth' });
			}
		},
		updateMinHeight(minHeight) {
			this.$refs.postGrid.style.minHeight = minHeight + 'px';
		},
	},
	mounted() {
		this.results = this.initial_results;
		if (this.showLoadMore) {
			this.currentPaged = 1;
		}
		if (typeof this.initial_model === 'undefined') return;
		if (typeof this.initial_model.paged !== 'undefined') {
			this.currentPaged = this.initial_model.paged;
		}
		if (typeof this.initial_model.total_pages !== 'undefined') {
			this.totalPages = this.initial_model.total_pages;
		}
		if (typeof this.initial_model.total_posts !== 'undefined') {
			this.totalPosts = this.initial_model.total_posts;
		}
		this.results = this.initial_results;
	},
};
</script>
